import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import './PublicCrechePage.css';

interface PlanoPublico {
  id: number;
  nome: string;
  periodicidade: 'mensal' | 'semestral' | 'anual';
  valor_base: number;
}

interface TurmaPublica {
  id: number;
  nome: string;
  faixa_etaria: string;
  turno: 'matutino' | 'vespertino' | 'integral';
}

interface CrechePublica {
  id: number;
  nome: string;
  nome_fantasia?: string;
  logo_url?: string | null;
  titulo?: string;
  texto_apresentacao?: string | null;
  telefone?: string;
  endereco: {
    logradouro?: string;
    numero?: string;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
    cep?: string;
  };
  planos: PlanoPublico[];
  turmas: TurmaPublica[];
}

const PERIODICIDADE_LABEL: Record<string, string> = {
  mensal: '/mês',
  semestral: '/semestre',
  anual: '/ano',
};

export default function PublicCrechePage() {
  const { slug } = useParams<{ slug: string }>();
  const [creche, setCreche] = useState<CrechePublica | null>(null);
  const [naoEncontrada, setNaoEncontrada] = useState(false);
  const [carregando, setCarregando] = useState(true);

  const [planoSelecionado, setPlanoSelecionado] = useState<number | null>(null);
  const [form, setForm] = useState({
    aluno_nome: '',
    data_nascimento: '',
    responsavel_nome: '',
    responsavel_telefone: '',
    responsavel_email: '',
    turma_pretendida: '',
    id_turma_pretendida: '',
    turno_pretendido: '',
    observacoes: '',
  });
  const [lgpdConsentimento, setLgpdConsentimento] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState(false);

  useEffect(() => {
    if (!slug) return;
    api
      .get<CrechePublica>(`/publico/creche/${slug}`)
      .then(({ data }) => setCreche(data))
      .catch(() => setNaoEncontrada(true))
      .finally(() => setCarregando(false));
  }, [slug]);

  function onChange(campo: keyof typeof form, valor: string) {
    setForm((prev) => ({ ...prev, [campo]: valor }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErro('');

    if (!form.aluno_nome.trim() || !form.responsavel_nome.trim() || !form.responsavel_telefone.trim()) {
      setErro('Nome da criança, nome e telefone do responsável são obrigatórios.');
      return;
    }
    if (!lgpdConsentimento) {
      setErro('É necessário concordar com o uso dos dados para enviar a pré-inscrição.');
      return;
    }

    setEnviando(true);
    try {
      await api.post(`/publico/creche/${slug}/pre-matricula`, {
        ...form,
        id_turma_pretendida: form.id_turma_pretendida || undefined,
        id_plano: planoSelecionado || undefined,
        lgpd_consentimento: lgpdConsentimento,
      });
      setSucesso(true);
    } catch (err: any) {
      setErro(err?.response?.data?.message || 'Não foi possível enviar sua pré-inscrição. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  }

  if (carregando) {
    return <div className="pc-loading">Carregando...</div>;
  }

  if (naoEncontrada || !creche) {
    return (
      <div className="pc-nao-encontrada">
        <h1>Página não encontrada</h1>
        <p>Esta creche não está disponível ou o link está incorreto.</p>
      </div>
    );
  }

  const enderecoCompleto = [
    creche.endereco.logradouro,
    creche.endereco.numero,
    creche.endereco.complemento,
    creche.endereco.bairro,
    creche.endereco.cidade && creche.endereco.estado
      ? `${creche.endereco.cidade}/${creche.endereco.estado}`
      : creche.endereco.cidade,
    creche.endereco.cep,
  ].filter(Boolean).join(', ');

  return (
    <div className="pc-root">
      <header className="pc-header">
        {creche.logo_url && <img src={creche.logo_url} alt={creche.nome} className="pc-logo" />}
        <h1>{creche.titulo || creche.nome_fantasia || creche.nome}</h1>
        {creche.texto_apresentacao && <p className="pc-apresentacao">{creche.texto_apresentacao}</p>}
        {creche.telefone && <p className="pc-telefone">📞 {creche.telefone}</p>}
      </header>

      {creche.planos.length > 0 && (
        <section className="pc-planos">
          <h2>Planos de Atendimento</h2>
          <div className="pc-planos-grid">
            {creche.planos.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`pc-plano-card ${planoSelecionado === p.id ? 'pc-plano-selecionado' : ''}`}
                onClick={() => setPlanoSelecionado(p.id)}
              >
                <strong>{p.nome}</strong>
                <span className="pc-plano-valor">
                  R$ {Number(p.valor_base).toFixed(2)} {PERIODICIDADE_LABEL[p.periodicidade]}
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="pc-form-section">
        {sucesso ? (
          <div className="pc-sucesso">
            <h2>✅ Pré-inscrição recebida!</h2>
            <p>A creche entrará em contato em breve para dar continuidade à matrícula.</p>
          </div>
        ) : (
          <>
            <h2>Pré-inscrição para Matrícula</h2>
            <form onSubmit={handleSubmit} className="pc-form">
              <p className="pc-form-section-title">Dados da Criança</p>
              <div className="pc-form-row">
                <label>Nome completo da criança
                  <input required value={form.aluno_nome} onChange={(e) => onChange('aluno_nome', e.target.value)} />
                </label>
                <label>Data de nascimento
                  <input type="date" value={form.data_nascimento} onChange={(e) => onChange('data_nascimento', e.target.value)} />
                </label>
              </div>
              <div className="pc-form-row">
                <label>Turma / faixa etária pretendida
                  {creche.turmas.length > 0 ? (
                    <select
                      value={form.id_turma_pretendida}
                      onChange={(e) => onChange('id_turma_pretendida', e.target.value)}
                    >
                      <option value="">Selecione...</option>
                      {creche.turmas.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.nome} ({t.faixa_etaria})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      value={form.turma_pretendida}
                      onChange={(e) => onChange('turma_pretendida', e.target.value)}
                      placeholder="Ex: Maternal II"
                    />
                  )}
                </label>
                <label>Turno pretendido
                  <select value={form.turno_pretendido} onChange={(e) => onChange('turno_pretendido', e.target.value)}>
                    <option value="">Selecione...</option>
                    <option value="matutino">Matutino</option>
                    <option value="vespertino">Vespertino</option>
                    <option value="integral">Integral</option>
                  </select>
                </label>
              </div>

              <p className="pc-form-section-title">Dados do Responsável</p>
              <div className="pc-form-row">
                <label>Nome completo
                  <input required value={form.responsavel_nome} onChange={(e) => onChange('responsavel_nome', e.target.value)} />
                </label>
                <label>Telefone / WhatsApp
                  <input required value={form.responsavel_telefone} onChange={(e) => onChange('responsavel_telefone', e.target.value)} />
                </label>
              </div>
              <label>E-mail
                <input type="email" value={form.responsavel_email} onChange={(e) => onChange('responsavel_email', e.target.value)} />
              </label>
              <label>Observações
                <textarea rows={3} value={form.observacoes} onChange={(e) => onChange('observacoes', e.target.value)} />
              </label>

              <label className="pc-consentimento">
                <input
                  type="checkbox"
                  checked={lgpdConsentimento}
                  onChange={(e) => setLgpdConsentimento(e.target.checked)}
                />
                {' '}Autorizo o uso destes dados (meus e da criança) exclusivamente para fins de
                pré-inscrição e contato pela creche, conforme a Lei Geral de Proteção de Dados (LGPD).
              </label>

              {erro && <div className="pc-erro">{erro}</div>}

              <button type="submit" className="pc-btn-enviar" disabled={enviando || !lgpdConsentimento}>
                {enviando ? 'Enviando...' : 'Enviar Pré-inscrição'}
              </button>
            </form>
          </>
        )}
      </section>

      <footer className="pc-footer">
        <p>{creche.nome}</p>
        {enderecoCompleto && <p>{enderecoCompleto}</p>}
      </footer>
    </div>
  );
}
